/*
 * Rate limiter for a page's save triggers: every trigger keeps its meaning,
 * but two saves never land within `floorMs`.
 *
 * Why it exists: on the Snap! pages the cost of saving turned out to be the
 * *number* of versions rather than the size of each. Measured on
 * code-csp-2026-2027 a month into the year, a saved version costs ~45KB on
 * disk — the deltas work fine, a project is multi-MB — but one student had
 * 682 versions of a single assignment, and that one assignment was 43% of a
 * 628MB repo. Snap! has several ways to "run" something and each saves, so
 * without a floor an actively-working student saves every few seconds.
 *
 * A trigger inside the window is **deferred to the end of it, never
 * dropped**. Dropping would mean a student who clicks the green flag and
 * shuts the lid has work that reached nothing, which the old 30s autosave
 * would have caught. `now` bypasses the floor for the paths where deferring
 * is worse than churn — leaving the page is the last chance to write
 * anything at all.
 */

export type SaveFloor = {
  // Ask to save. Respects the floor unless `now`.
  request(opts?: { now?: boolean }): void;
  // Whether a deferred save is armed (for tests and diagnostics).
  deferred(): boolean;
};

export function saveFloor(opts: {
  // Minimum gap between saves; 0 or less disables the floor.
  floorMs: number;
  // Whether there is anything worth saving. Called on every request and
  // again when a deferred save fires, so an edit that was undone in the
  // meantime costs nothing.
  isDirty: () => boolean;
  save: () => void;
}): SaveFloor {
  const { floorMs, isDirty, save } = opts;
  // undefined, not 0: "never saved" has to be its own state rather than
  // riding on Date.now() being a large number, or the very first save is
  // deferred wherever the clock starts near the epoch.
  let lastSaveAt: number | undefined;
  let timer: number | undefined;

  const request = (o: { now?: boolean } = {}): void => {
    if (!isDirty()) return;
    const since = lastSaveAt === undefined ? Number.POSITIVE_INFINITY : Date.now() - lastSaveAt;
    if (o.now || floorMs <= 0 || since >= floorMs) {
      // A bypass supersedes anything pending, so the deferred save can't
      // fire a second write moments later.
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
      lastSaveAt = Date.now();
      save();
      return;
    }
    // Inside the window: arm the trailing edge once, and let every further
    // trigger collapse into it.
    if (timer === undefined) {
      timer = setTimeout(() => {
        timer = undefined;
        request({ now: true });
      }, floorMs - since) as unknown as number;
    }
  };

  return { request, deferred: () => timer !== undefined };
}
